import React, { useEffect, useState } from 'react'
import { isEmpty } from 'lodash-es'
import { useTranslation } from 'react-i18next'
import { Box, Callout, RowsSkeletonLoader } from '@node-space/ui-components'
import { TransactionsContextProvider } from 'contexts/transactionsContext'
import { useGetWalletTransactions } from 'hooks/queries/useGetWalletTransactions'
import { usePaginationNextPageItemCount } from 'hooks/usePaginationNextPageItemCount'
import { pageListOptions } from 'pages/WalletDetails/constants/contants'
import { CustomerWallet } from 'types/customers'
import { WalletType } from 'types/types'
import {
  AmplitudeEvent,
  AmplitudeEventAction,
  AmplitudeEventCategory,
} from 'utils/amplitude/amplitudeEvents'
import track from 'utils/tracker'
import { NoTransactions } from './NoTransactions/NoTransactions'
import { TransactionsPagination } from './TransactionsPagination'
import { TransactionTable } from './TransactionTable'
import { WalletTransactionExport } from './WalletTransactionsExport/WalletTransactionExport'

interface RecentActivityProps {
  wallet: WalletType
  customerWallet?: CustomerWallet
  isLoading?: boolean
}

export const RecentActivity = ({ wallet, customerWallet, isLoading }: RecentActivityProps) => {
  const [page, setPage] = useState(0)
  const [numOfRows, setNumOfRows] = useState(pageListOptions[0].value)

  const { data, isFetching, isError } = useGetWalletTransactions({
    wallet,
    size: Number(numOfRows),
    page,
  })

  const { t } = useTranslation()
  useEffect(() => {
    track.Amp.track(AmplitudeEvent.WALLET_DETAILS_TRANSACTION, {
      category: AmplitudeEventCategory.MERCHANT_PORTAL,
      action: AmplitudeEventAction.VIEW,
    })
  }, [])

  const nextPageRowCount = usePaginationNextPageItemCount({
    offset: data?.paging?.offset,
    page: data?.paging?.offset,
    total: data?.paging?.total,
    max: data?.paging?.max,
  })

  if (isError) {
    return (
      <div className="pb-5">
        <Callout variant="error">{t('oopsSomethingWentWrong')}</Callout>
      </div>
    )
  }

  if (isEmpty(data?.transactions) && !isFetching && !isLoading) {
    return <NoTransactions />
  }

  return (
    <Box flexCol testId="recent-activity-screen">
      <TransactionsContextProvider transactions={data?.transactions || []}>
        <Box paddingB={24}>
          <WalletTransactionExport customerWallet={customerWallet} />
        </Box>
        {isFetching || isLoading ? (
          <RowsSkeletonLoader rows={nextPageRowCount} />
        ) : (
          <TransactionTable />
        )}
      </TransactionsContextProvider>
      {data?.paging?.total > Number(pageListOptions[0].value) && (
        <TransactionsPagination
          page={page}
          setPage={setPage}
          pagination={data?.paging}
          nrOfRows={numOfRows}
          setNrOfRows={setNumOfRows}
        />
      )}
    </Box>
  )
}
